/* eslint-disable react/prop-types */

import React, { useEffect, useState, useRef } from "react";
import * as echarts from "echarts";
import deepmerge from "deepmerge";

import styled from "styled-components";

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
`;

export const AIChart = ({ spec }) => {
  const containerRef = useRef(null);
  const viewRef = useRef(null);
  const oldSpecJSON = useRef("{}");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    let runtime;
    let resizeObserver;
    try {
      // Initialize chart only once
      runtime = echarts.init(containerRef.current);
      viewRef.current = runtime;

      // ResizeObserver watches the container itself for any size changes
      resizeObserver = new ResizeObserver(() => {
        if (viewRef.current) {
          viewRef.current.resize();
        }
      });
      resizeObserver.observe(containerRef.current);
    } catch (err) {
      console.error("Error initializing chart:", err);
      setError(`Error initializing chart: ${err.message}`);
    }

    return () => {
      resizeObserver?.disconnect();
      if (viewRef.current) {
        viewRef.current.dispose();
        viewRef.current = null;
      }
    };
  }, []); // Only run once on mount

  // Update chart data when spec changes
  useEffect(() => {
    if (
      viewRef.current &&
      spec &&
      JSON.stringify(spec) !== oldSpecJSON.current
    ) {
      try {
        const safeSeries = spec.series?.map((s) =>
          s.type === "sankey" && s.links
            ? { ...s, links: s.links.filter((l) => l.source !== l.target) }
            : s
        );
        const safeSpec = safeSeries ? { ...spec, series: safeSeries } : spec;
        const mergedOptions = deepmerge.all([
          safeSpec,
          {
            grid: {
              containLabel: true,
              outerBoundsContain: "all",
              outerBoundsMode: "same",
              xAxisName: {
                show: false,
              },
              yAxisName: {
                show: false,
              },
            },
            tooltip: {
              confine: true,
            },
            toolbox: {
              show: true,
              feature: {
                dataZoom: { show: true },
                saveAsImage: { show: true, name: "chart" },
              },
            },
          },
        ]);
        viewRef.current.setOption(mergedOptions, { notMerge: true });
        setError(null);
        oldSpecJSON.current = JSON.stringify(spec);
      } catch (err) {
        console.error("Error updating chart:", err);
        setError(`Error updating chart: ${err.message}`);
      }
    }
  }, [spec]);

  return (
    <ChartContainer>
      {error && <div>{error}</div>}
      <div style={{ width: "100%", height: "300px" }} ref={containerRef} />
    </ChartContainer>
  );
};
